/**
 * v260.104 — Bulk file → fields import wizard.
 *
 * When a user drops MULTIPLE files (≥ 2) on any RichTextEditor inside
 * the Requirements form, instead of importing the first one we open
 * this modal. It lists every dropped file, pre-matches each one to a
 * likely target field by fuzzy-matching filename ↔ field label, and
 * lets the user adjust the mapping + per-file insert/replace mode in
 * one go.
 *
 * Why a context provider (`BulkImportProvider`) instead of threading
 * props down from the page? The Requirements form is a ~2200-LOC
 * monolith and the editors are nested deep — context keeps the wire-
 * up local to the components that need it without bubbling state
 * through every intermediate render layer.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, ArrowRight, FileText, FileType2, Upload, Search, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const BulkImportCtx = createContext({
  registerField: () => () => {},
  openWithFiles: () => {},
});

export function useBulkImport() {
  return useContext(BulkImportCtx);
}

// Lightweight fuzzy-match: lowercase + strip non-alnum, then check
// whether the filename's normalised form contains the field-label's
// normalised form, or vice-versa. Good enough to auto-route
// "ExecSummary.docx" → "Executive Summary" without imports / deps.
function _normalise(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}
function _autoMatch(filename, fields) {
  const base = _normalise(filename.replace(/\.[^.]+$/, ''));
  if (!base) return '';
  let best = '';
  let bestScore = 0;
  for (const f of fields) {
    const norm = _normalise(f.label);
    if (!norm) continue;
    // Substring containment in either direction.
    let score = 0;
    if (base.includes(norm)) score = norm.length;
    else if (norm.includes(base)) score = base.length;
    else {
      // Word-overlap bonus (so "exec_summary.docx" matches
      // "Executive Summary" even though neither is a substring of
      // the other).
      const a = base.split(/[^a-z0-9]+/);
      const b = norm.split(/[^a-z0-9]+/);
      score = a.filter(w => w && b.includes(w)).join('').length;
    }
    if (score > bestScore) { bestScore = score; best = f.id; }
  }
  return best;
}

export function BulkImportProvider({ children }) {
  // Keyed by stable `field.id`. Each entry is the current set of
  // properties registered by a mounted NarrativeField: { id, label,
  // currentValue, setValue }. We re-set on every register call so
  // the setter closure stays fresh — closures captured at mount
  // would otherwise stale-out and overwrite with old onChange refs.
  const registryRef = useRef(new Map());
  const [files, setFiles] = useState(null);
  const [rows, setRows] = useState([]);  // [{ file, fieldId, mode }]
  const [busy, setBusy] = useState(false);
  // v260.105 — Search + sort UI state. Cleared whenever the dialog
  // opens with a new file set so big drops don't inherit a stale
  // filter from a previous session.
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');  // 'all' | 'mapped' | 'unmapped'

  const registerField = useCallback((entry) => {
    if (!entry || !entry.id) return () => {};
    registryRef.current.set(entry.id, entry);
    return () => {
      const cur = registryRef.current.get(entry.id);
      // Only deregister if the entry we set is still the latest one
      // (avoids race with rapid re-mounts).
      if (cur && cur === entry) registryRef.current.delete(entry.id);
    };
  }, []);

  const openWithFiles = useCallback((fileList) => {
    const arr = Array.from(fileList || []).filter(f => {
      const n = (f.name || '').toLowerCase();
      return n.endsWith('.md') || n.endsWith('.markdown') || n.endsWith('.txt') || n.endsWith('.docx');
    });
    if (arr.length === 0) {
      toast.error('No importable files — drop .md, .txt, or .docx');
      return;
    }
    const fieldOpts = Array.from(registryRef.current.values());
    setRows(arr.map(f => ({ file: f, fieldId: _autoMatch(f.name, fieldOpts), mode: 'replace' })));
    setQuery('');
    setFilter('all');
    setFiles(arr);
  }, []);

  const close = () => { setFiles(null); setRows([]); setQuery(''); setFilter('all'); };

  const runImport = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    setBusy(true);
    let ok = 0, skip = 0, fail = 0;
    // v260.108 — Capture each touched field's previous value BEFORE
    // we mutate it. The `Undo last import` toast action then iterates
    // these snapshots and restores via the same `setValue` setter.
    // We snapshot at apply-time (not when the row was queued) so a
    // user who edits a field between dialog-open and Import-click
    // still gets their latest content back on undo.
    const undoSnapshots = [];
    for (const row of rows) {
      if (!row.fieldId) { skip += 1; continue; }
      const entry = registryRef.current.get(row.fieldId);
      if (!entry) { skip += 1; continue; }
      const name = row.file.name.toLowerCase();
      try {
        let md;
        if (name.endsWith('.md') || name.endsWith('.markdown') || name.endsWith('.txt')) {
          md = (await row.file.text()).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        } else {
          const fd = new FormData();
          fd.append('file', row.file);
          const r = await fetch(`${API}/api/files/to-markdown`, {
            method: 'POST', credentials: 'include', body: fd,
          });
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error(err.detail || `HTTP ${r.status}`);
          }
          md = (await r.json()).markdown || '';
        }
        const existing = entry.currentValue || '';
        const next = row.mode === 'replace'
          ? md
          : (existing ? `${existing}\n\n${md}` : md);
        undoSnapshots.push({ fieldId: row.fieldId, previousValue: existing });
        entry.setValue(next);
        ok += 1;
      } catch (err) {
        console.error('bulk-import row failed:', err);
        fail += 1;
      }
    }
    setBusy(false);
    if (ok > 0) {
      // v260.108 — Show success with an Undo action that lives for
      // 30 s. Sonner auto-dismisses when duration elapses or the
      // user clicks the action.
      const onUndo = () => {
        let restored = 0;
        for (const snap of undoSnapshots) {
          const entry = registryRef.current.get(snap.fieldId);
          if (!entry) continue;  // field unmounted — skip silently
          entry.setValue(snap.previousValue);
          restored += 1;
        }
        if (restored > 0) toast.success(`Undid ${restored} field${restored === 1 ? '' : 's'}`);
        else toast.error('Could not undo — target fields are no longer mounted.');
      };
      toast.success(
        `Imported ${ok} file${ok === 1 ? '' : 's'}${fail || skip ? ` (${fail} failed, ${skip} skipped)` : ''}`,
        { duration: 30000, action: { label: 'Undo', onClick: onUndo } },
      );
    } else {
      toast.error(`No files imported — ${fail} failed, ${skip} skipped`);
    }
    close();
  };

  const ctxValue = useMemo(() => ({ registerField, openWithFiles }), [registerField, openWithFiles]);
  const fieldOpts = Array.from(registryRef.current.values());
  // v260.105 — Derive the visible row set: apply search + status
  // filter, but keep the ORIGINAL index attached so each visible
  // row still mutates the right entry in `rows` when changed.
  const q = query.trim().toLowerCase();
  const visibleRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => {
    if (q && !r.file.name.toLowerCase().includes(q)) return false;
    if (filter === 'mapped' && !r.fieldId) return false;
    if (filter === 'unmapped' && r.fieldId) return false;
    return true;
  });
  const mappedCount = rows.filter(x => x.fieldId).length;
  const unmappedCount = rows.length - mappedCount;
  // v260.105 — "Map remaining by best-guess" sweeps every currently
  // unmapped row through the same fuzzy matcher used at drop time.
  // Useful when users have cleared a few rows manually and want to
  // refresh suggestions without redoing the drop.
  const mapRemaining = () => {
    let changed = 0;
    setRows(prev => prev.map(r => {
      if (r.fieldId) return r;
      const guess = _autoMatch(r.file.name, fieldOpts);
      if (guess) changed += 1;
      return guess ? { ...r, fieldId: guess } : r;
    }));
    if (changed > 0) toast.success(`Auto-mapped ${changed} more file${changed === 1 ? '' : 's'}`);
    else toast.info('No additional matches found — pick the targets manually.');
  };

  return (
    <BulkImportCtx.Provider value={ctxValue}>
      {children}
      <Dialog open={!!files} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-3xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-[#4ABFCF]" />
              Import {rows.length} file{rows.length === 1 ? '' : 's'} into fields
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-3">
            We've pre-matched each file to a likely target based on its filename. Adjust any row, pick replace vs append, then commit them all at once.
          </div>
          {/* v260.105 — Search + sort toolbar. Only render when more
              than a handful of rows are present so small drops keep
              the dialog compact. */}
          {rows.length > 4 && (
            <div className="flex items-center gap-2 mb-2" data-testid="bulk-import-toolbar">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by filename…"
                  className="w-full text-xs pl-7 pr-2 py-1.5 border border-slate-200 rounded-md focus:border-[#4ABFCF] focus:ring-1 focus:ring-[#4ABFCF]/30 outline-none"
                  data-testid="bulk-import-search"
                />
              </div>
              <div className="flex items-center gap-0.5 text-[11px] bg-slate-100 rounded-md p-0.5" data-testid="bulk-import-filter">
                {[
                  { k: 'all', label: 'All', count: rows.length },
                  { k: 'mapped', label: 'Mapped', count: mappedCount },
                  { k: 'unmapped', label: 'Unmapped', count: unmappedCount },
                ].map(({ k, label, count }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setFilter(k)}
                    className={`px-2 py-0.5 rounded transition-colors ${
                      filter === k ? 'bg-white text-[#0F2040] shadow-sm font-medium' : 'text-slate-500 hover:text-[#0F2040]'
                    }`}
                    data-testid={`bulk-import-filter-${k}`}
                  >
                    {label} <span className="tabular-nums text-slate-400">{count}</span>
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={mapRemaining}
                disabled={unmappedCount === 0}
                className="text-[11px] h-7 gap-1"
                title="Run the filename auto-matcher across every unmapped row"
                data-testid="bulk-import-map-remaining"
              >
                <Wand2 className="w-3 h-3" /> Map remaining
              </Button>
            </div>
          )}
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {visibleRows.map(({ r, i }) => (
              <BulkRow
                key={`${r.file.name}-${i}`}
                row={r}
                idx={i}
                fields={fieldOpts}
                onChange={(updates) => setRows(prev => prev.map((x, j) => j === i ? { ...x, ...updates } : x))}
                onRemove={() => setRows(prev => prev.filter((_, j) => j !== i))}
              />
            ))}
            {visibleRows.length === 0 && (
              <div className="text-xs text-slate-400 italic text-center py-4">
                {rows.length === 0
                  ? 'All rows removed — close the dialog or drop more files.'
                  : 'No files match the current filter.'}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <span className="text-[11px] text-slate-400">
              {mappedCount} of {rows.length} mapped to a field
              {q || filter !== 'all' ? ` · showing ${visibleRows.length}` : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={close} disabled={busy} data-testid="bulk-import-cancel">Cancel</Button>
              <Button
                onClick={runImport}
                disabled={busy || rows.length === 0 || mappedCount === 0}
                data-testid="bulk-import-commit"
              >
                {busy ? 'Importing…' : `Import ${mappedCount}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </BulkImportCtx.Provider>
  );
}

function BulkRow({ row, idx, fields, onChange, onRemove }) {
  const isDocx = row.file.name.toLowerCase().endsWith('.docx');
  const Icon = isDocx ? FileType2 : FileText;
  const kbSize = (row.file.size / 1024).toFixed(1);
  // v260.106 — Lazy content preview. The first time the user hovers
  // the filename we read the first 240 chars of the file (locally
  // for .md/.txt, via /api/files/to-markdown for .docx) and cache
  // the result on this row. Stops the "commit → check → redo" loop
  // when filenames are cryptic (e.g. "v2_FINAL_FINAL.docx").
  const [preview, setPreview] = useState({ status: 'idle', text: '' });
  const loadPreview = async () => {
    if (preview.status !== 'idle') return;
    setPreview({ status: 'loading', text: '' });
    try {
      const name = row.file.name.toLowerCase();
      let md;
      if (name.endsWith('.md') || name.endsWith('.markdown') || name.endsWith('.txt')) {
        md = (await row.file.text()).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      } else {
        const fd = new FormData();
        fd.append('file', row.file);
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/files/to-markdown`, {
          method: 'POST', credentials: 'include', body: fd,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        md = (await r.json()).markdown || '';
      }
      const trimmed = md.length > 240 ? md.slice(0, 240).trimEnd() + '…' : md;
      setPreview({ status: 'ready', text: trimmed || '(empty file)' });
    } catch (err) {
      setPreview({ status: 'error', text: String(err.message || err) });
    }
  };
  return (
    <div className="grid grid-cols-[1fr_auto_1.4fr_auto_auto] items-center gap-2 p-2 rounded-md border border-slate-100 bg-slate-50/40">
      <div
        className="relative flex items-center gap-2 min-w-0 group/preview"
        onMouseEnter={loadPreview}
      >
        <Icon className="w-4 h-4 shrink-0 text-slate-500" />
        <div className="min-w-0">
          <div className="text-xs font-medium text-[#0F2040] truncate">{row.file.name}</div>
          <div className="text-[10px] text-slate-400">{kbSize} KB · hover to preview</div>
        </div>
        {/* CSS-only hover popover — appears above the row so it
            doesn't push siblings around. `pointer-events: none`
            keeps it from blocking row interactions while shown. */}
        <div
          className="absolute left-0 top-full mt-1 z-30 hidden group-hover/preview:block pointer-events-none w-[28rem] max-w-[80vw] rounded-md border border-slate-200 bg-white shadow-lg p-2.5 text-[11px]"
          data-testid={`bulk-import-preview-${idx}`}
        >
          <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Content preview</div>
          {preview.status === 'idle' && (
            <div className="text-slate-400 italic">Loading…</div>
          )}
          {preview.status === 'loading' && (
            <div className="text-slate-400 italic">Loading…</div>
          )}
          {preview.status === 'ready' && (
            <pre className="whitespace-pre-wrap font-mono text-[10.5px] text-slate-700 leading-snug">{preview.text}</pre>
          )}
          {preview.status === 'error' && (
            <div className="text-rose-600">Could not load preview: {preview.text}</div>
          )}
        </div>
      </div>
      <ArrowRight className="w-3 h-3 text-slate-300" />
      <select
        className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:border-[#4ABFCF] focus:ring-1 focus:ring-[#4ABFCF]/30"
        value={row.fieldId}
        onChange={(e) => onChange({ fieldId: e.target.value })}
        data-testid={`bulk-import-target-${idx}`}
      >
        <option value="">— skip —</option>
        {fields.map(f => (
          <option key={f.id} value={f.id}>{f.label}</option>
        ))}
      </select>
      <div className="flex items-center gap-1 text-[10px]">
        {['replace', 'append'].map(m => (
          <button
            key={m}
            type="button"
            onClick={() => onChange({ mode: m })}
            className={`px-2 py-0.5 rounded-md border ${
              row.mode === m
                ? (m === 'replace' ? 'bg-rose-50 border-rose-300 text-rose-700' : 'bg-cyan-50 border-cyan-300 text-cyan-700')
                : 'border-slate-200 text-slate-500 hover:bg-slate-50'
            }`}
            data-testid={`bulk-import-mode-${idx}-${m}`}
          >
            {m === 'replace' ? 'Replace' : 'Append'}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50"
        title="Remove from import"
        data-testid={`bulk-import-remove-${idx}`}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

/**
 * Helper hook for NarrativeField (or any other RTE host) to register
 * its current `value` + `onChange` setter so the bulk-import dialog
 * can write into it later. Returns a no-op when the registry context
 * isn't present (e.g., editor used outside the requirements form) so
 * single-field drag-drop still works.
 */
export function useRegisterBulkImportField({ id, label, value, setValue }) {
  const { registerField } = useBulkImport();
  useEffect(() => {
    if (!id || !label || !setValue) return undefined;
    return registerField({ id, label, currentValue: value, setValue });
  }, [id, label, value, setValue, registerField]);
}
